import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useMasterCodeSeries } from 'hooks/api';
import type { MasterCodeSeriesDto } from 'api/types';

// ============================================================================
// MASTER CODE NUMBERING — prefix and digit width of the automatic customer and
// service codes, with the last code issued. Saving changes new codes only; it
// never renumbers an existing record and never resets a series' serial.
// ============================================================================

const SERIES_LABEL: Record<string, string> = {
  PARTY: 'Party (customer, supplier, employee)',
  SERVICE: 'Service',
  REIMBURSEMENT: 'Reimbursable item',
};

const PREFIX_PATTERN = /^[A-Za-z0-9]{1,10}$/;

interface Draft { prefix: string; digitWidth: string }

const note: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };
const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem', width: '100%' };

const MasterCodeNumberingModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { series, loading, save } = useMasterCodeSeries();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const draftOf = (s: MasterCodeSeriesDto): Draft =>
    drafts[s.seriesKey] ?? { prefix: s.prefix, digitWidth: String(s.digitWidth) };

  const patchDraft = (s: MasterCodeSeriesDto, patch: Partial<Draft>) => {
    setDrafts(prev => ({ ...prev, [s.seriesKey]: { ...draftOf(s), ...patch } }));
    setErrors(prev => ({ ...prev, [s.seriesKey]: '' }));
  };

  // Client-side check mirrors the server rules; the server still validates on save.
  const validate = (s: MasterCodeSeriesDto): string | null => {
    const d = draftOf(s);
    if (!PREFIX_PATTERN.test(d.prefix.trim())) return 'The prefix must be 1 to 10 letters or digits.';
    const width = Number(d.digitWidth);
    if (!Number.isInteger(width) || width < 1 || width > 10) return 'The number of digits must be between 1 and 10.';
    return null;
  };

  const confirmSave = async () => {
    const s = series.find(x => x.seriesKey === pendingKey);
    if (!s) { setPendingKey(null); return; }
    setSaving(true);
    try {
      const d = draftOf(s);
      await save(s.seriesKey, { prefix: d.prefix.trim().toUpperCase(), digitWidth: Number(d.digitWidth) });
      setDrafts(prev => { const next = { ...prev }; delete next[s.seriesKey]; return next; });
      notificationService.toast.success(`${SERIES_LABEL[s.seriesKey] ?? s.seriesKey} numbering saved.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the numbering.');
    } finally {
      setSaving(false);
      setPendingKey(null);
    }
  };

  const requestSave = (s: MasterCodeSeriesDto) => {
    const error = validate(s);
    if (error) { setErrors(prev => ({ ...prev, [s.seriesKey]: error })); return; }
    setPendingKey(s.seriesKey);
  };

  const pendingLabel = SERIES_LABEL[pendingKey ?? ''] ?? pendingKey ?? '';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Document numbering</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ ...note, marginBottom: '0.75rem' }}>
            Automatic codes are the prefix followed by a serial. A new record takes the next serial; a code already in use is skipped.
            A change applies to new codes only. Existing codes are not renumbered.
          </div>

          {loading && <div style={note}>Loading…</div>}

          {!loading && series.map(s => {
            const d = draftOf(s);
            const dirty = d.prefix !== s.prefix || d.digitWidth !== String(s.digitWidth);
            return (
              <div key={s.seriesKey} className="form-group" data-field={s.seriesKey} style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>{SERIES_LABEL[s.seriesKey] ?? s.seriesKey}</label>
                <div className="form-grid-3" style={{ alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Prefix</label>
                    <input className={`form-input${errors[s.seriesKey] ? ' form-field-error' : ''}`} value={d.prefix} maxLength={10}
                      onChange={e => patchDraft(s, { prefix: e.target.value.toUpperCase() })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Digits</label>
                    <input className="form-input" type="number" min={1} max={10} value={d.digitWidth}
                      onChange={e => patchDraft(s, { digitWidth: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <button className="btn btn-primary" disabled={!dirty || saving} onClick={() => requestSave(s)}
                      style={{ fontSize: '0.8rem', padding: '0.375rem 0.75rem', width: '100%' }}>
                      Save
                    </button>
                  </div>
                </div>
                {errors[s.seriesKey] && <div className="form-error-message">{errors[s.seriesKey]}</div>}
                <div style={{ ...note, marginTop: '0.35rem' }}>
                  Last issued: <strong style={{ color: 'var(--text-primary)' }}>{s.lastCode ?? 'none yet'}</strong>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingKey !== null}
        title="Save numbering"
        message={`Save the ${pendingLabel.toLowerCase()} numbering? New records will use this prefix and digit count from now on.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Saving..."
        onConfirm={confirmSave}
        onCancel={() => setPendingKey(null)}
      />
    </>
  );
};

export default MasterCodeNumberingModal;
